import {
    PsHttpService,
    type PatSnapSuccessResponseData,
} from '@patsnap/tahiti-core/common/ps-http';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import type {
    DocumentType,
    WorkMode,
    PatentRegion,
    StyleCategory,
    StyleSourceType,
    AttachmentCategory,
} from '../../constants/enum';

// ==================== Request Types ====================

/**
 * 使用量信息
 */
export interface UsageInfo {
    /** 总量 */
    total: number;
    /** 已使用量 */
    usage: number;
}

/**
 * 时区信息
 */
export interface TimeZoneInfo {
    /** 时区值 */
    value: string;
    /** UTC偏移量（分钟） */
    utc_offset: number;
}

/**
 * 权限信息
 */
export interface PermissionInfo {
    /** AI翻译权限 */
    ai_translation: boolean;
    /** AI交底书权限 */
    ai_disclosure: boolean;
    /** AI中国说明书权限 */
    ai_specification_cn: boolean;
    /** AI美国说明书权限 */
    ai_specification_us: boolean;
    /** AI欧洲说明书权限 */
    ai_specification_eu: boolean;
    /** AI新颖性检索权限 */
    ai_novelty_search: boolean;
    /** AI FTO检索权限 */
    ai_fto_search: boolean;
    /** AI统一说明书权限 */
    ai_specification_unified: boolean;
}

/**
 * 旧版起草初始化数据响应
 */
export interface OldDraftingInitDataResponse {
    /** 时区信息 */
    time_zone: TimeZoneInfo;
    /** 权限信息 */
    permission: PermissionInfo;
    /** 新颖性检索使用量 */
    novelty_search_usage: UsageInfo;
    /** FTO检索使用量 */
    fto_search_usage: UsageInfo;
    /** 翻译使用量 */
    translation_usage: UsageInfo;
    /** 专利交底书使用量 */
    patent_disclosure_usage: UsageInfo;
    /** 中国专利说明书使用量 */
    cn_patent_specification_usage: UsageInfo;
    /** 美国专利说明书使用量 */
    us_patent_specification_usage: UsageInfo;
    /** 欧洲专利说明书使用量 */
    eu_patent_specification_usage: UsageInfo;
    /** 统一专利说明书使用量 */
    unified_patent_specification_usage: UsageInfo;
    /** 统一使用量 */
    unified_usage: UsageInfo;
    /** 统一专业版使用量 */
    unified_pro_usage: UsageInfo;
    /** 统一免费版使用量 */
    unified_free_usage: UsageInfo;
    /** 图像检索使用量 */
    image_search_usage: UsageInfo;
    /** 层级代码 */
    tier_code: string;
}

/**
 * 创建任务请求参数
 */
export interface CreateDraftingJobRequest {
    /** 用户输入的文本内容 */
    prompt_text: string;
    /** 模板ID */
    template_id?: number;
    /** 项目标题（为空则自动生成） */
    title?: string;
    /** 附件ID列表 */
    file_ids?: string[];
    /** 写作风格ID */
    style_id?: number;
    /** 文档类型 */
    document_type?: DocumentType;
    /** 工作模式 */
    work_mode?: WorkMode;
    /** 专利ID */
    patent_id?: string;
    /** 专利地区 */
    patent_region?: string;
    /** 来源入口 */
    start_from?: string;
}

/**
 * 创建任务响应
 */
export interface CreateDraftingJobResponse {
    /** 项目ID */
    project_id: string;
    /** 文档ID */
    document_id: string;
    /** 会话ID */
    conversation_id: string;
    /** 标题 */
    title: string;
    /** 状态 */
    status: string;
    /** 创建时间 */
    created_at: string;
}

/**
 * 获取模板列表请求参数
 */
export interface GetTemplateListRequest {
    /** 页码 */
    page?: number;
    /** 每页数量 */
    limit?: number;
    /** 专利局/地区 */
    region?: string;
    /** 模板类型 */
    template_type?: string;
    /** 技术领域 */
    technology_area?: string;
    /** 模板来源 */
    source?: string;
    /** 来源筛选：MY=我的模板，LIBRARY=模板库 */
    from_source?: 'MY' | 'LIBRARY';
    /** 关键词搜索 */
    keyword?: string;
    /** 仅显示已收藏的模板 */
    favorites_only?: boolean;
    brief?: boolean;
}

/**
 * 模板区块信息
 */
export interface TemplateSection {
    /** 区块ID */
    id: string;
    /** 区块类型 */
    type: string;
    /** 排序顺序 */
    order: number;
    /** 区块标题 */
    title: string;
    /** 区块内容 */
    content: string;
    /** 是否必填 */
    required: boolean;
    /** AI 提示词 */
    ai_prompt: string;
}

/**
 * 模板信息
 */
export interface Template {
    /** 模板ID */
    id: number;
    /** 创建时间 */
    created_at: string;
    /** 更新时间 */
    updated_at: string;
    /** 是否已删除 */
    is_deleted: boolean;
    /** 删除时间 */
    deleted_at: string | null;
    /** 删除人 */
    deleted_by: string | null;
    /** 创建人 */
    created_by: string;
    /** 更新人 */
    updated_by: string;
    /** 模板名称 */
    name: string;
    /** 模板中文名称 */
    name_cn: string;
    /** 模板描述 */
    description: string;
    /** 模板中文描述 */
    description_cn: string;
    /** 模板来源 */
    source: string;
    /** 模板类型 */
    template_type: string;
    /** 专利局/地区 */
    region: string;
    /** 技术领域 */
    technology_area: string;
    /** 模板内容（HTML） */
    content: string;
    /** 模板区块列表 */
    sections: TemplateSection[];
    /** 用户ID */
    user_id: string;
    /** 公司ID */
    company_id: string;
    /** 是否激活 */
    is_active: boolean;
    /** 是否已收藏 */
    is_favorited: boolean;
    /** 收藏ID */
    favorite_id: string | null;
}

/**
 * 获取模板详情请求参数
 */
export interface GetTemplateDetailRequest {
    /** 模板ID */
    id: string | number;
}

/**
 * 获取模板列表响应
 */
export interface GetTemplateListResponse {
    /** 模板列表 */
    items: Template[];
    /** 总数 */
    total: number;
    /** 当前页码 */
    page: number;
    /** 每页数量 */
    limit: number;
    /** 总页数 */
    total_pages: number;
}

/**
 * 获取样式列表请求参数
 */
export interface GetWritingStyleListRequest {
    /** 页码 */
    page?: number;
    /** 每页数量 */
    limit?: number;
    /** 分类：system/user/all */
    category?: StyleCategory;
    /** 专利局/地区：US/CN/EP/JP/KR/WIPO */
    region?: PatentRegion;
    /** 关键词搜索（名称或描述） */
    keyword?: string;
    /** 仅显示收藏 */
    favorites_only?: boolean;
}

/**
 * 样式来源 - 手动创建
 */
export interface WritingStyleSourceManual {
    /** 来源类型 */
    type: StyleSourceType.MANUAL;
}

/**
 * 样式来源 - 专利引用
 */
export interface WritingStyleSourceReference {
    /** 来源类型 */
    type: StyleSourceType.REFERENCE;
    /** 引用的专利列表 */
    patents: {
        /** 专利标题 */
        title: string;
        /** 专利ID */
        patent_id: string;
        /** 专利号 */
        patent_number: string;
    }[];
}

/**
 * 样式来源（联合类型）
 */
export type WritingStyleSource = WritingStyleSourceManual | WritingStyleSourceReference;

/**
 * 写作样式信息
 */
export interface WritingStyle {
    /** 样式ID */
    id: number;
    /** 样式名称 */
    name: string;
    /** 样式描述 */
    description: string;
    /** 分类：system/user/all */
    category: string;
    /** 专利局/地区 */
    region: string;
    /** 样式来源 */
    source: WritingStyleSource;
    /** 公司标签 */
    company_tags: string[];
    /** 行业标签 */
    industry_tags: string[];
    /** 是否公开 */
    is_public: boolean;
    /** 是否收藏 */
    is_favorite: boolean;
    /** 使用次数 */
    usage_count: number;
    /** 创建时间 */
    created_at: string;
    /** 更新时间 */
    updated_at: string;
}

/**
 * 获取样式列表响应
 */
export interface GetWritingStyleListResponse {
    /** 样式列表 */
    items: WritingStyle[];
    /** 总数 */
    total: number;
    /** 当前页码 */
    page: number;
    /** 每页数量 */
    limit: number;
    /** 总页数 */
    total_pages: number;
}

/**
 * 获取样式详情请求参数
 */
export interface GetWritingStyleDetailRequest {
    /** 样式ID */
    id: string | number;
}

/**
 * 写作样式章节定义
 */
export interface WritingStyleSections {
    /** 标题章节样式 */
    title?: string;
    /** 权利要求章节样式 */
    claims?: string;
    /** 摘要章节样式 */
    summary?: string;
    /** 简要说明章节样式 */
    abstract?: string;
    /** 附图说明章节样式 */
    drawings?: string;
    /** 背景技术章节样式 */
    background?: string;
    /** 技术领域章节样式 */
    technicalField?: string;
    /** 具体实施方式章节样式 */
    detailedDescription?: string;
}

/**
 * 获取样式详情响应
 */
export interface GetWritingStyleDetailResponse {
    /** 样式ID */
    id: number;
    /** 样式名称 */
    name: string;
    /** 样式描述 */
    description: string;
    /** 分类：system/user/all */
    category: string;
    /** 专利局/地区 */
    region: string;
    /** 章节样式定义 */
    sections: WritingStyleSections;
    /** 样式来源 */
    source: WritingStyleSource;
    /** 公司标签 */
    company_tags: string[];
    /** 行业标签 */
    industry_tags: string[];
    /** 是否公开 */
    is_public: boolean;
    /** 是否收藏 */
    is_favorite: boolean;
    /** 使用次数 */
    usage_count: number;
    /** 用户ID */
    user_id: string;
    /** 公司ID */
    company_id: string;
    /** 是否激活 */
    is_active: boolean;
    /** 创建时间 */
    created_at: string;
    /** 更新时间 */
    updated_at: string;
}

/**
 * 专利搜索请求参数
 */
export interface SearchPatentByPnRequest {
    /** 专利公开号 */
    pn: string;
}

/**
 * 专利缩略图信息
 */
export interface PatentThumbnail {
    /** 类型 */
    type: string;
    /** 图片URL */
    url: string;
    /** 图片ID */
    image_id: string;
}

/**
 * 专利摘要来源信息
 */
export interface PatentAbstractSource {
    /** 是否来自OCR */
    isOCR: boolean;
}

/**
 * 专利基础信息
 */
export interface PatentBasicInfo {
    /** 标题语言 */
    TITLE_LANG: string;
    /** 摘要语言 */
    ABST_LANG: string;
    /** 申请人（标准化） */
    ANCS: string[];
    /** 发明人 */
    IN: string[];
    /** 摘要语言列表 */
    ABST_LANGS: string[];
    /** 申请人 */
    AN: string[];
    /** 公开日期 */
    PBD: string;
    /** 申请日期 */
    APD: string;
    /** 申请人（标准化） */
    ANC: string[];
    /** 发明人语言 */
    IN_LANG: string;
    /** 缩略图 */
    THUMB: PatentThumbnail | string;
    /** 标题 */
    TITLE: string;
    /** 摘要（HTML格式） */
    ABST: string;
    /** 专利号 */
    PN: string;
    /** 摘要来源 */
    ABST_SOURCE: PatentAbstractSource;
    /** 标题语言列表 */
    TITLE_LANGS: string[];
    /** 申请号 */
    APN: string;
}

/**
 * 专利搜索结果项
 */
export interface PatentSearchResultItem {
    /** 专利ID */
    patent_id: string;
    /** 专利基础信息 */
    basic_info: PatentBasicInfo;
}

/**
 * 专利搜索响应
 */
export interface SearchPatentByPnResponse {
    /** 搜索的专利号 */
    pn: string;
    /** 搜索结果总数 */
    total: number;
    /** 专利列表 */
    results: PatentSearchResultItem[];
}

/**
 * 上传附件请求参数
 */
export interface UploadAttachmentRequest extends Record<string, any> {
    /** 上传的文件 */
    file: File | Blob;
    /** 项目ID（可选），如果提供则将附件与项目关联 */
    project_id?: string;
    /** 附件类型分类（可选），如'Disclosure', 'Application', 'Prosecution', 'Other' */
    category?: AttachmentCategory;
}

/**
 * 上传附件响应
 */
export interface UploadAttachmentResponse {
    /** 文件ID */
    file_id: string;
    /** 文件名 */
    file_name: string;
    /** 文件类型 */
    file_type: string;
    /** 文件大小（字节） */
    file_size: number;
    /** 签名URL */
    signed_url: string | null;
    /** URL过期时间 */
    url_expires_at: string | null;
    /** 处理状态 */
    process_status: string;
    /** 附件分类 */
    category: string;
    /** 文件摘要 */
    summary: string | null;
    /** 来源类型 */
    source_type: string;
    /** 创建时间 */
    created_at: string;
    /** 文件访问URL */
    file_url: string;
    /** 文件存储键 */
    file_key: string;
    /** 是否已关联 */
    is_associated: boolean | null;
    /** 元数据 */
    metadata: Record<string, any>;
}

// ==================== API Interface ====================

export interface DraftingApi {
    /**
     * 获取旧版起草初始化数据
     */
    getOldDraftingInitData(): Promise<OldDraftingInitDataResponse>;

    /**
     * 创建任务（支持交底书提交、专利申请、审查意见答复等）
     */
    createDraftingJob(data: CreateDraftingJobRequest): Promise<CreateDraftingJobResponse>;

    /**
     * 获取模板列表
     */
    getTemplateList(params: GetTemplateListRequest): Promise<GetTemplateListResponse>;

    /**
     * 获取模板详情
     */
    getTemplateDetail(data: GetTemplateDetailRequest): Promise<Template>;

    /**
     * 获取样式列表
     */
    getWritingStyleList(params: GetWritingStyleListRequest): Promise<GetWritingStyleListResponse>;

    /**
     * 获取样式详情
     */
    getWritingStyleDetail(data: GetWritingStyleDetailRequest): Promise<GetWritingStyleDetailResponse>;

    /**
     * 根据专利号搜索专利
     */
    searchPatentByPn(data: SearchPatentByPnRequest): Promise<SearchPatentByPnResponse>;

    /**
     * 上传附件
     */
    uploadAttachment(
        options: UploadAttachmentRequest,
        onProgress?: (e: any) => void
    ): Promise<UploadAttachmentResponse>;
}

export const DraftingApi = Symbol('DraftingApi');

// ==================== API Implementation ====================

@injectable()
export class DraftingApiImpl extends PsHttpService implements DraftingApi {
    get options() {
        return {
            baseURL: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/drafting/api`,
            headers: {
                'Content-Type': 'application/json',
            },
        };
    }

    getOldDraftingInitData() {
        return this.get<PatSnapSuccessResponseData<any>>({
            url: `${import.meta.env.VITE_CORE_SEARCH_SERVICE_URI}/core-telecom-api/telecom/ai-drafting/init`,
        }).then((res) => {
            return res.data;
        });
    }

    createDraftingJob(data: CreateDraftingJobRequest) {
        return this.post<PatSnapSuccessResponseData<CreateDraftingJobResponse>>({
            url: '/home/start',
            data,
        }).then((res) => {
            return res as unknown as CreateDraftingJobResponse;
        });
    }

    getTemplateList(params: GetTemplateListRequest) {
        return this.get<PatSnapSuccessResponseData<GetTemplateListResponse>>({
            url: '/templates/list',
            params,
        }).then((res) => {
            return res.data;
        });
    }

    getTemplateDetail(data: GetTemplateDetailRequest) {
        return this.get<PatSnapSuccessResponseData<Template>>({
            url: `/templates/${data.id}`,
        }).then((res) => {
            return res.data;
        });
    }

    getWritingStyleList(params: GetWritingStyleListRequest) {
        return this.get<PatSnapSuccessResponseData<GetWritingStyleListResponse>>({
            url: '/writing-styles/list',
            params,
        }).then((res) => {
            return res.data;
        });
    }

    getWritingStyleDetail(data: GetWritingStyleDetailRequest) {
        return this.get<PatSnapSuccessResponseData<GetWritingStyleDetailResponse>>({
            url: `/writing-styles/${data.id}`,
        }).then((res) => {
            return res.data;
        });
    }

    searchPatentByPn(data: SearchPatentByPnRequest) {
        return this.post<PatSnapSuccessResponseData<SearchPatentByPnResponse>>({
            url: '/patent/search-by-pn',
            data,
        }).then((res) => {
            return res.data;
        });
    }

    uploadAttachment(options: UploadAttachmentRequest, onProgress?: (e: any) => void) {
        const formData = new FormData();
        for (const key in options) {
            formData.append(key, (options as any)[key]);
        }
        return this.post<PatSnapSuccessResponseData<UploadAttachmentResponse>>({
            url: '/attachments/upload',
            data: formData,
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: onProgress,
        }).then((res) => {
            return res.data;
        });
    }
}
